import { useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import IconButton from '@mui/material/IconButton';
import DeleteIcon from '@mui/icons-material/Delete';
import { Table } from '@components/Table';
import TableContainer from '@mui/material/TableContainer';
import { Typography } from '@components/Typography';
import Button from '@components/Button';
import Autocomplete from '@components/Autocomplete';
import { CSVToArray } from '@helpers/csv-helper';
import { validateBulkData, removeUnchangedContacts, findDuplicate } from '@helpers/bulk-contacts-upload';
import { alertActions } from '@actions';
import { contactsService } from '@services';
import { useTranslation, useLocations } from '@hooks';
import { handleApiError } from '@helpers/api';
import { Modal, FileInputContainer } from './styles';

const baseStyles = { maxWidth: '222px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' };

const ContactsColumnNames = t => {
    return [
        {
            key: 1,
            component: (
                <Typography align="left" variant="subtitle2">
                    {t('common.firstName')}
                </Typography>
            ),
        },
        {
            key: 2,
            component: (
                <Typography align="left" variant="subtitle2">
                    {t('common.lastName')}
                </Typography>
            ),
        },
        {
            key: 3,
            component: (
                <Typography align="left" variant="subtitle2">
                    {t('common.phoneNumber')}
                </Typography>
            ),
        },
        {
            key: 4,
            component: (
                <Typography align="left" variant="subtitle2">
                    {t('common.emailAddress')}
                </Typography>
            ),
        },
        {
            key: 6,
            component: (
                <Typography align="left" variant="subtitle2">
                    {t('ContactsPage.facebookProfile')}
                </Typography>
            ),
        },
        {
            key: 7,
            component: (
                <Typography align="left" variant="subtitle2">
                    {t('ContactsPage.instagramProfile')}
                </Typography>
            ),
        },
        {
            key: 8,
            component: (
                <Typography align="left" variant="subtitle2">
                    {t('common.externalId')}
                </Typography>
            ),
        },
        {
            key: 9,
            component: <Typography align="left" variant="subtitle2"></Typography>,
        },
    ];
};

const cell = (isChanged, oldValue, newValue, resultColor) => {
    return !isChanged ? (
        <Typography color={resultColor} variant="body2" align="left" style={{ ...baseStyles }}>
            {newValue || '--'}
        </Typography>
    ) : (
        <>
            <Typography
                color={resultColor}
                variant="body2"
                align="left"
                style={{
                    ...baseStyles,
                    textDecoration: 'line-through',
                }}
            >
                {oldValue || '--'}
            </Typography>
            <Typography color={'success'} variant="body2" align="left" style={{ ...baseStyles }}>
                {newValue || '--'}
            </Typography>
        </>
    );
};

const contactRows = (model, onDelete) =>
    model.contacts.map((contact, index) => {
        const duplicate = findDuplicate(contact, model.duplicates);
        let phoneColor = contact.phoneValid || !contact.phoneNumber ? 'textPrimary' : 'error';
        let emailColor = contact.emailValid || !contact.email ? 'textPrimary' : 'error';
        let resultColor = 'textPrimary';
        const changed = {
            firstName: false,
            lastName: false,
            phoneNumber: false,
            email: false,
            facebook: false,
            instagram: false,
            externalId: false,
        };
        if (duplicate && contact.phoneValid && contact.emailValid) {
            Object.entries(changed).forEach(([key]) => {
                if (contact[key] && contact[key] !== duplicate[key]) {
                    changed[key] = true;
                }
                if (!contact[key] && duplicate[key]) {
                    contact[key] = duplicate[key];
                }
            });
        }
        const components = [];
        Object.entries(changed).forEach(([key, value]) => {
            const color = key === 'phoneNumber' ? phoneColor : key === 'email' ? emailColor : resultColor;
            components.push({
                component: cell(value, duplicate?.[key], contact[key], color),
            });
        });
        components.push({
            component: (
                <IconButton
                    color={!contact.phoneValid || !contact.emailValid ? 'error' : 'primary'}
                    onClick={() => onDelete(index)}
                >
                    <DeleteIcon />
                </IconButton>
            ),
        });
        return components;
    });

const CsvLink = 'https://docs.google.com/spreadsheets/d/1elW5JQyXmByeMzRHhhsSuX3PXL42HyxGhNytbWXfVsI/';

const BulkUploadModal = ({ isOpen, setModal, locale, onUpdateCallback }) => {
    const { t } = useTranslation();
    const { getLocationsOptions } = useLocations();
    const [modalState, setModalState] = useState({ isExpectingFile: true, showContactList: false });
    const [importModel, setImportModel] = useState({ contacts: [], duplicates: [] });
    const [selectedLocations, setSelectedLocations] = useState([]);
    const [fileName, setFileName] = useState('');

    const dispatch = useDispatch();

    const locationsOptions = useMemo(() => getLocationsOptions(), []);

    const isListValid = useMemo(() => {
        return (
            importModel.contacts.length > 0 &&
            importModel.contacts.every(
                person => (person.emailValid && person.phoneValid) || (person.emailValid && !person.phoneNumber),
            )
        );
    }, [importModel.contacts]);

    const onCloseModal = () => setModal(false);

    const onDeleteContact = newValue => {
        const contactsCopy = [...importModel.contacts];
        contactsCopy.splice(newValue, 1);
        setImportModel(prev => ({ ...prev, contacts: contactsCopy }));
    };

    const submitCSV = event => {
        const file = event.target.files[0];
        setFileName(file.name);
        let fileReader = new FileReader();
        const processData = async () => {
            const data = fileReader.result;
            const list = validateBulkData(CSVToArray(data), locale);
            if (!list || list.length === 0) {
                dispatch(alertActions.error(t('validation.wrongFile')));
            } else {
                const savedContacts = await getDuplicates(list);
                const response = removeUnchangedContacts(list, savedContacts);
                setImportModel(response);
                setModalState(prev => ({ ...prev, isExpectingFile: false }));
            }
        };
        fileReader.onloadend = processData;
        fileReader.readAsText(file);

        event.target.value = null;
    };

    const getDuplicates = async contacts => {
        try {
            const data = await contactsService.getDuplicates(contacts);
            return data;
        } catch (error) {
            handleApiError(error);
        }
    };

    const importContacts = () => setModalState(prev => ({ ...prev, showContactList: true }));

    const uploadContacts = async () => {
        try {
            const locations = selectedLocations.map(location => location.id);
            const contactsUpdated = importModel.contacts.map(contact => {
                const duplicate = findDuplicate(contact, importModel.duplicates);
                if (duplicate) {
                    Object.entries(contact).forEach(([key]) => {
                        if (!contact[key] && duplicate[key]) {
                            contact[key] = duplicate[key];
                        }
                    });
                    return {
                        ...contact,
                        id: duplicate.id,
                        labels: duplicate.labels,
                        //companies: [...new Set([...duplicate.companies, ...locations])],
                    };
                }
                return contact; //{ ...contact, /*companies: locations */};
            });
            await contactsService.importContactsBulk({ contacts: contactsUpdated, locations });
            dispatch(alertActions.success(t('alertMessages.importSuccess')));
            onUpdateCallback();
        } catch (error) {
            handleApiError(error, 'alertMessages.importFail');
        }
        setModal(false);
    };

    return (
        <>
            <Modal
                isOpen={isOpen}
                handleClose={onCloseModal}
                showContactList={modalState.showContactList}
                title={t('ContactsPage.importTitle')}
                subtitle={
                    <Typography variant="caption" color="textSecondary">
                        {t('ContactsPage.importSubtitle')}{' '}
                        <a href={CsvLink} target="_blank" rel="noreferrer" style={{ color: '#1F4C5C' }}>
                            {t('ContactsPage.bulkFileLink')}
                        </a>
                    </Typography>
                }
                onPrimaryAction={!modalState.showContactList ? importContacts : uploadContacts}
                primaryActionText={
                    !modalState.showContactList ? t('ContactsPage.validate') : t('ContactsPage.importFile')
                }
                primaryActionDisabled={
                    selectedLocations.length === 0 || modalState.isExpectingFile
                        ? true
                        : modalState.showContactList
                        ? !isListValid
                        : false
                }
                onSecondaryAction={onCloseModal}
                secondaryActionText={t('buttons.cancel')}
            >
                {modalState.showContactList ? (
                    <>
                        <Typography
                            variant="caption"
                            sx={{ marginBottom: '24px', display: 'block', color: 'rgba(0, 0, 0, 0.6)' }}
                        >
                            {t('ContactsPage.dataVerificationDescription')}
                        </Typography>
                        <TableContainer>
                            <Table
                                withHover
                                columnNames={ContactsColumnNames(t)}
                                rows={contactRows(importModel, onDeleteContact)}
                            />
                        </TableContainer>
                    </>
                ) : (
                    <>
                        <Autocomplete
                            label={t('common.locations')}
                            options={locationsOptions}
                            value={selectedLocations}
                            isOptionEqualToValue={(option, value) => option.id === value.id}
                            inputProps={{ style: { display: selectedLocations?.length > 0 ? 'none' : 'block' } }}
                            onChangeAutoComplete={items => setSelectedLocations(items.length ? items : [])}
                            groupMultiProps={{
                                getOptionLabel: option => option.label,
                            }}
                            required
                            className="companies-autocomplete"
                        />
                        <Typography variant="caption" color="textSecondary">
                            {t('ContactsPage.csvFile')}
                        </Typography>
                        <FileInputContainer>
                            <input
                                accept=".csv,.xlsx,.xls"
                                style={{ display: 'none' }}
                                id="raised-button-file"
                                type="file"
                                onChange={submitCSV}
                                name="contactsList"
                            />
                            <label htmlFor="raised-button-file">
                                <Button type="button" component="span" fullWidth variant="outlined">
                                    {t('buttons.uploadCSVFile')}
                                </Button>
                            </label>
                            <Typography variant="caption" color="textSecondary">
                                {fileName}
                            </Typography>
                        </FileInputContainer>
                        <Typography variant="caption" color="textSecondary">
                            {importModel.contacts?.length > 0 && importModel.duplicates?.length >= 0
                                ? t('ContactsPage.numberToImport', {
                                      diff: importModel.contacts?.length - importModel.duplicates?.length,
                                      duplicates: importModel.duplicates?.length,
                                  })
                                : t('ContactsPage.noContacts')}
                        </Typography>
                    </>
                )}
            </Modal>
        </>
    );
};

export default BulkUploadModal;
